import { Controller } from '@hotwired/stimulus';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import AwsS3 from '@uppy/aws-s3';
import { heicTo } from 'heic-to';
//import ImageEditor from '@uppy/image-editor'

export default class UppyController extends Controller {
  static targets = ['button', 'container', 'preview', 'removeButton', 'imagesList'];
  static values = {
    allowedFileType: { type: String, default: 'image/*' },
    bucket: String,
    hiddenInput: String,
    maxFiles: { type: Number, default: 100 },
    autoUpload: { type: Boolean, default: true },
    imageableId: Number,
    imageableType: String,
    documentUpload: { type: Boolean, default: false },
    draggable: { type: Boolean, default: true },
  };

  connect() {
    if (window.env === 'development') console.log('Uppy controller connected', this);

    this.images = [];

    // Build allowed file types array - always start with images
    // Include .heic/.heif explicitly since image/* may not match on all browsers
    let allowedFileTypes = ['image/*', '.heic', '.heif'];

    // Add custom file type if provided
    if (this.allowedFileTypeValue !== 'image/*') {
      allowedFileTypes.push(this.allowedFileTypeValue);
    }

    // Add document types if document upload mode
    if (this.documentUploadValue) {
      allowedFileTypes.push(
        '.pdf',
        '.doc',
        '.docx',
        '.txt',
        '.rtf',
        '.odt',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      );
    }

    this.uppy = new Uppy({
      restrictions: {
        maxNumberOfFiles: this.maxFilesValue,
        allowedFileTypes: allowedFileTypes,
        maxFileSize: 25 * 1024 * 1024,
      },
    });

    this.uppy.use(Dashboard, {
      target: this.containerTarget,
      proudlyDisplayPoweredByUppy: false,
      inline: true,
      height: 300,
    });

    //this.uppy.use(ImageEditor)

    this.uppy.use(AwsS3, {
      getUploadParameters: this.getUploadParameters.bind(this),
    });

    this.uppy.on('upload-success', this.handleUploadSuccess.bind(this));
    this.uppy.on('file-added', this.handleFileAdded.bind(this));
    this.uppy.on('complete', this.handleCompleteUpload.bind(this));

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.addEventListener('click', this.handleRemove.bind(this));
      this.removeButtonTarget.type = 'button';
    }

    // Initialize drag and drop for existing images
    if (window.env === 'development') console.log('draggableValue:', this.draggableValue, typeof this.draggableValue);
    this.initializeDragAndDrop();
    this.updateButtonStates();
  }

  disconnect() {
    this.uppy?.close && this.uppy.close();
  }

  getUploadParameters(file, options) {
    return fetch('/uploads/s3', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      },
      body: JSON.stringify({
        bucket: this.bucketValue,
        filename: file.name,
        contentType: file.type,
      }),
    }).then((response) => response.json());
  }

  handleRemove(event) {
    event.preventDefault();

    // Create and append hidden input with value '' to wipe column on save
    const hiddenField = document.createElement('input');
    hiddenField.type = 'hidden';
    hiddenField.name = this.hiddenInputValue;
    hiddenField.value = '';

    // Append hidden input to container
    this.containerTarget.appendChild(hiddenField);

    if (this.hasRemoveButtonTarget) {
      this.removeButtonTarget.innerHTML = 'Save to remove image';
      this.removeButtonTarget.disabled = true;
    }
  }

  handleUploadSuccess(file, response) {
    const url = response.uploadURL;
    this.uploadedFiles = this.uploadedFiles || [];
    this.uploadedFiles.push(url);
  }

  async handleCompleteUpload(result) {
    if (this.uploadedFiles && this.uploadedFiles.length > 0) {
      try {
        const response = await fetch('/images', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
            Accept: 'text/html',
          },
          body: JSON.stringify({
            imageable_type: this.imageableTypeValue,
            imageable_id: this.imageableIdValue,
            s3_urls: this.uploadedFiles,
            document_upload: this.documentUploadValue,
          }),
        });

        if (response.ok) {
          const html = await response.text();
          if (this.hasImagesListTarget) {
            this.imagesListTarget.insertAdjacentHTML('beforeend', html);
          }
          this.uploadedFiles = [];

          // Auto-click Done button after 3 seconds
          setTimeout(() => {
            const doneButton = this.containerTarget.querySelector('.uppy-StatusBar-actionBtn--done');
            if (doneButton) {
              doneButton.click();
            }
          }, 3000);

          // Re-initialize drag and drop for new images
          this.initializeDragAndDrop();
          this.updateButtonStates();
        } else {
          console.error('Failed to save images to database');
        }
      } catch (error) {
        console.error('Error saving images:', error);
      }
    }
  }

  async deleteImage(event) {
    if (!confirm('Are you sure you want to delete this image?')) {
      return;
    }

    const imageId = event.currentTarget.dataset.imageId;
    const imageElement = event.currentTarget.closest('.uploaded-img');

    // Build URL with document_upload parameter if needed
    let deleteUrl = `/images/${imageId}`;
    if (this.documentUploadValue) {
      deleteUrl += '?document_upload=true';
    }

    try {
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
        },
      });

      if (response.ok) {
        imageElement.remove();
        this.updateButtonStates();
      } else {
        console.error('Failed to delete image');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
    }
  }

  moveUp(event) {
    const imageId = event.currentTarget.dataset.imageId;
    const imageElement = event.currentTarget.closest('.uploaded-img');
    const previousElement = imageElement.previousElementSibling;

    if (previousElement) {
      this.imagesListTarget.insertBefore(imageElement, previousElement);
      this.updateImageRanks();
      this.updateButtonStates();
    }
  }

  moveDown(event) {
    const imageId = event.currentTarget.dataset.imageId;
    const imageElement = event.currentTarget.closest('.uploaded-img');
    const nextElement = imageElement.nextElementSibling;

    if (nextElement) {
      this.imagesListTarget.insertBefore(nextElement, imageElement);
      this.updateImageRanks();
      this.updateButtonStates();
    }
  }

  makePrimary(event) {
    const imageId = event.currentTarget.dataset.imageId;
    const imageElement = event.currentTarget.closest('.uploaded-img');

    // Move the image to the first position
    this.imagesListTarget.insertBefore(imageElement, this.imagesListTarget.firstElementChild);

    // Update ranks and button states
    this.updateImageRanks();
    this.updateButtonStates();
  }

  updateButtonStates() {
    if (!this.hasImagesListTarget) return;

    const imageItems = this.imagesListTarget.querySelectorAll('.uploaded-img');

    imageItems.forEach((item, index) => {
      const isPrimary = index === 0;

      // Update move buttons
      const moveUpBtn = item.querySelector('[data-action*="moveUp"]');
      const moveDownBtn = item.querySelector('[data-action*="moveDown"]');

      if (moveUpBtn) {
        moveUpBtn.classList.toggle('d-none', isPrimary);
      }

      if (moveDownBtn) {
        moveDownBtn.classList.toggle('d-none', index === imageItems.length - 1);
      }

      // Update primary image label and make primary button
      const primaryLabel = item.querySelector('.primary-image-label');
      const makePrimaryBtn = item.querySelector('.make-primary-btn');

      if (primaryLabel) {
        primaryLabel.style.display = isPrimary ? 'inline-block' : 'none';
      }

      if (makePrimaryBtn) {
        makePrimaryBtn.style.display = isPrimary ? 'none' : 'inline-block';
      }
    });
  }
  handleFileAdded(file) {
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || /\.hei[cf]$/i.test(file.name);

    if (isHeic) {
      heicTo({ blob: file.data, type: 'image/jpeg', quality: 0.8 })
        .then((convertedImage) => {
          this.uppy.removeFile(file.id);
          this.uppy.addFile({
            name: file.name.replace(/\.hei[cf]$/i, '.jpg'),
            type: 'image/jpeg',
            data: convertedImage,
          });
        })
        .catch((error) => {
          console.error('Error converting HEIC file:', error);
          this.uppy.removeFile(file.id);
        });
    } else if (this.autoUploadValue) {
      this.uppy.upload();
    }
  }

  initializeDragAndDrop() {
    if (!this.hasImagesListTarget) return;
    if (!this.draggableValue) return;

    // Store bound functions for proper event listener management
    if (!this.boundDragHandlers) {
      this.boundDragHandlers = {
        dragStart: this.handleDragStart.bind(this),
        dragOver: this.handleDragOver.bind(this),
        dragEnter: this.handleDragEnter.bind(this),
        dragLeave: this.handleDragLeave.bind(this),
        drop: this.handleDrop.bind(this),
        dragEnd: this.handleDragEnd.bind(this),
        containerDragOver: this.handleContainerDragOver.bind(this),
        containerDrop: this.handleContainerDrop.bind(this),
      };
    }

    const imageItems = this.imagesListTarget.querySelectorAll('.uploaded-img');

    imageItems.forEach((item) => {
      // Skip if already initialized
      if (item.dataset.dragInitialized) return;

      item.draggable = this.draggableValue;
      if (this.draggableValue) item.style.cursor = 'move';
      item.dataset.dragInitialized = 'true';

      item.addEventListener('dragstart', this.boundDragHandlers.dragStart);
      item.addEventListener('dragover', this.boundDragHandlers.dragOver);
      item.addEventListener('dragenter', this.boundDragHandlers.dragEnter);
      item.addEventListener('dragleave', this.boundDragHandlers.dragLeave);
      item.addEventListener('drop', this.boundDragHandlers.drop);
      item.addEventListener('dragend', this.boundDragHandlers.dragEnd);
    });

    // Add container-level listeners for better Firefox support
    if (!this.imagesListTarget.dataset.containerDragInitialized) {
      this.imagesListTarget.addEventListener('dragover', this.boundDragHandlers.containerDragOver);
      this.imagesListTarget.addEventListener('drop', this.boundDragHandlers.containerDrop);
      this.imagesListTarget.dataset.containerDragInitialized = 'true';
    }
  }

  handleDragStart(event) {
    // Don't initiate drag when interacting with form elements (e.g. inline title edit)
    const tag = event.target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') {
      event.preventDefault();
      return;
    }

    this.draggedElement = event.currentTarget;
    this.originalNextSibling = event.currentTarget.nextSibling;
    event.currentTarget.style.opacity = '0.5';
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', event.currentTarget.dataset.imageId || '');

    if (window.env === 'development') console.log('Drag started for image:', event.currentTarget.dataset.imageId);
  }

  handleDragEnter(event) {
    if (event.currentTarget === this.draggedElement) return;
    event.preventDefault();
  }

  handleDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    if (event.currentTarget === this.draggedElement) return;

    // Only do visual reordering in Chrome-like browsers
    // Firefox will handle this in the drop event
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    if (!isFirefox) {
      const afterElement = this.getDragAfterElement(this.imagesListTarget, event.clientY);
      if (afterElement == null) {
        this.imagesListTarget.appendChild(this.draggedElement);
      } else {
        this.imagesListTarget.insertBefore(this.draggedElement, afterElement);
      }
    }
  }

  handleDragLeave(event) {
    // Firefox-specific handling
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (window.env === 'development') console.log('Drop event fired');

    // For Firefox, do the reordering here
    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
    if (isFirefox && event.currentTarget !== this.draggedElement) {
      const afterElement = this.getDragAfterElement(this.imagesListTarget, event.clientY);
      if (afterElement == null) {
        this.imagesListTarget.appendChild(this.draggedElement);
      } else {
        this.imagesListTarget.insertBefore(this.draggedElement, afterElement);
      }
    }

    this.updateImageRanks();
    this.updateButtonStates();
  }

  handleDragEnd(event) {
    event.currentTarget.style.opacity = '1';
    this.draggedElement = null;
    this.originalNextSibling = null;

    if (window.env === 'development') console.log('Drag ended');
  }

  handleContainerDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  handleContainerDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (window.env === 'development') console.log('Container drop event fired');

    // Ensure we update ranks even if individual drop event doesn't fire
    setTimeout(() => {
      this.updateImageRanks();
      this.updateButtonStates();
    }, 10);
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.uploaded-img:not([style*="opacity: 0.5"])')];

    return draggableElements.reduce(
      (closest, child) => {
        const box = child.getBoundingClientRect();
        const offset = y - box.top - box.height / 2;

        if (offset < 0 && offset > closest.offset) {
          return { offset: offset, element: child };
        } else {
          return closest;
        }
      },
      { offset: Number.NEGATIVE_INFINITY }
    ).element;
  }

  async updateImageRanks() {
    if (window.env === 'development')
      console.log('updateImageRanks called, checking for imagesListTarget:', this.hasImagesListTarget);

    if (!this.hasImagesListTarget) return;

    const imageItems = this.imagesListTarget.querySelectorAll('.uploaded-img');
    if (window.env === 'development') console.log('Found image items:', imageItems.length);

    const updates = [];

    imageItems.forEach((item, index) => {
      const imageId = item.dataset.imageId;
      const newRank = index + 1;
      const currentRank = parseInt(item.dataset.rank);

      if (window.env === 'development') {
        console.log(`Image ${imageId}: current rank ${currentRank} -> new rank ${newRank}`);
      }

      if (currentRank !== newRank) {
        item.dataset.rank = newRank;
        updates.push({ id: imageId, rank: newRank });
      }
    });

    if (window.env === 'development') console.log('Updates to make:', updates.length);

    if (updates.length > 0) {
      try {
        await Promise.all(updates.map((update) => this.updateImageRank(update.id, update.rank)));
        if (window.env === 'development') console.log('Successfully updated image ranks:', updates);
      } catch (error) {
        console.error('Error updating image ranks:', error);
      }
    } else {
      if (window.env === 'development') console.log('No rank updates needed');
    }
  }

  async updateImageRank(imageId, rank) {
    const response = await fetch(`/images/${imageId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
      },
      body: JSON.stringify({
        image: { rank: rank },
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to update rank for image ${imageId}`);
    }

    return response.json();
  }
}
